import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {ReportScriptTransportOrderQuery} from "./ReportScriptTransportOrderQuery";
import AceEditor from "react-ace";
import * as XLSX from 'xlsx';

import 'brace/theme/monokai'
import 'brace/ext/searchbox'
import ReportScriptResultTabMenu from "./ReportScriptResultTabMenu";
import {ConfigContext} from "../../../context/ConfigContext";
import {clampTabIndex} from "../../../context/TabSession";
import {Button, ButtonGroup, Icon, Tab} from "semantic-ui-react";
import {OutputReport, QueryResponse, ReportRow} from "../../../../../generated";
import {authentication} from "../../../../../authentication";
import {v4 as uuid} from 'uuid';
import {TransportOrderSearch} from "../../transportOrders/TransportOrderSearch";
import {useHistory} from 'react-router-dom';
import {ChangeableSplitter} from "../../../../util/table-splitter-ui/src/components/Splitters/ChangableSplitter";
import {
    ReportScriptSelection,
    selectedReportScript,
    selectedReportScriptPath
} from "./ReportScriptSelection";
import {updateConfig, workingConfig} from "../../configChange/ConfigSignal";
import {selectedReportScriptPage} from "./ReportScriptResults";


export interface ReportScriptPageProps {
    location: any
    match: any
}

export function ReportScriptPage(props: React.PropsWithChildren<ReportScriptPageProps>) {

    const history = useHistory()
    const context = useContext(ConfigContext)

    const [isLoading, setIsLoading] = useState(false)


    const [xRequestId, setXRequestId] = React.useState("")

    const [transportOrders, setTransportOrders] = useState([])

    const [response, setResponse] = useState<OutputReport>(null)
    const [error, setError] = useState<string>(null)

    const [transportOrdersOfSearch, setTransportOrdersOfSearch] = useState([])

    useEffect(() => {
        if (context.reportScriptResults != null) {
            setResponse(context.reportScriptResults)
        }
    }, [])

    async function execute() {
        setIsLoading(true)
        let url = '/converter/generateTransportOrderReport'

        let request = {
            orders: transportOrders,
            script: selectedReportScript.value,
            reportName: "TestReport",
            companyId: authentication.company._id
        }

        let response = await fetch(url, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json; charset=utf-8",
                'x-request-id': uuid()
            },
            body: JSON.stringify(request)
        })
        setXRequestId(response.headers.get("x-request-id"))

        if (response.status == 200) {
            setError(null)
            let result = JSON.parse(await response.text())
            setResponse(result)
            context.setReportScriptResults(result)
        } else {
            let e = response.status + ": " + response.statusText
            setResponse(null)
            setError(e)
        }
        setIsLoading(false)
    }

    function jsonToCsv(items: any) {
        const header = Object.keys(items[0]);
        const headerString = header.join(';');
        // handle null or undefined values here
        const replacer = (key: string, value: string) => value ?? '';
        const rowItems = items.map((row: any) =>
            header
                .map((fieldName) => JSON.stringify(row[fieldName], replacer))
                .join(';')
        );
        // join header and body, and break into separate lines
        return [headerString, ...rowItems].join('\r\n');
    }


    async function download() {
        let page = selectedReportScriptPage.value
        let currentRecords = response.pages[page].dataLines
        let headers = response.pages[page].header

        if (currentRecords && headers) {
            const headerRow = headers.map(h => h.stringContent)
            const dataRows = currentRecords.map((row: ReportRow) =>
                row.cells.map(cell => cell.stringContent)
            )

            const worksheetData = [headerRow, ...dataRows]
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, response.pages[page].name || "Sheet1")

            XLSX.writeFile(workbook, `${response.pages[page].name}.xlsx`)
        }
    }

    function onQueryUpdate(queryResponse: QueryResponse) {
        setTransportOrders(queryResponse.transportOrders)
    }

    function drawQueryBox() {

        const panes = [
            {
                menuItem: 'Query',
                render: () => <Tab.Pane>
                    <ReportScriptTransportOrderQuery
                        onUpdate={onQueryUpdate}
                    />
                </Tab.Pane>
            },
            {
                menuItem: 'SimpleSearch',
                render: () => <Tab.Pane>
                    <TransportOrderSearch
                        selectedOrderIdsChanged={(newSelectedOrderIds) => {
                            setTransportOrders(transportOrdersOfSearch.filter(to => newSelectedOrderIds.includes(to._id)))
                        }}
                        selectedOrderChanged={(newSelectedOrder) => {
                        }}
                        foundTransportOrdersChanged={(newFoundOrders) => {
                            setTransportOrdersOfSearch(newFoundOrders)
                        }}
                        reload={(page, take) => {
                        }}
                        history={history}
                        initialPageSize={5}
                        hideArchiveButton={true}
                        location={props.location}
                        match={props.match}
                        preSelectedTransportOrderIds={[]}
                        allowMultipleSelection={true}
                    />
                </Tab.Pane>
            }]
        return <Tab
            id={"ReportScriptPageTab"}
            panes={panes}
            className={"flexTab"}
            defaultActiveIndex={clampTabIndex(context.tabSession.reportScriptTab, panes)}
            onTabChange={(_, data) => {
                context.setSingleTabSession("reportScriptTab", parseInt(data.activeIndex.toString()))
                setTransportOrders([])
            }}
        />

    }


    function saveNewScript(newScript) {
        //console.log("saveNewScript: ", newScript)
        const jp = require('jsonpath');
        let config = JSON.parse(workingConfig.value)
        jp.value(config, selectedReportScriptPath.value, newScript)
        updateConfig(
            JSON.stringify(config, null, 4),
            true,
            false
        ).then(r => {
        })
    }

    function drawScriptBox() {
        if(selectedReportScriptPath.value == null) {
            return <>
                <h2>Select a report script in the dropDown below!</h2>
            </>
        }
        return <>
            <AceEditor
                theme="monokai"
                mode="groovy"
                value={selectedReportScript.value}
                width={"100%"}
                height={"100%"}
                onChange={(e) => {
                    selectedReportScript.value = e
                    saveNewScript(e)
                }}
            />
        </>
    }

    function drawTop() {
        return <>
            <ChangeableSplitter
                name={"ReportScriptPageTopSplitter"}
                initiallyHorizontal={false}
                topOrLeft={drawQueryBox()}
                bottomOrRight={drawScriptBox()}
            />
        </>
    }

    function drawBottom() {
        return <div
            style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "scroll"
            }}
        >
            <ButtonGroup>

                <Button
                    disabled={selectedReportScript.value == "" || transportOrders.length == 0}
                    style={{
                        backgroundColor: "teal",
                        width: "60%"
                    }}
                    onClick={execute}
                    loading={isLoading}
                >
                    Execute
                </Button>
                <Button
                    disabled={response == null}
                    style={{
                        backgroundColor: "green",
                        width: "39%"
                    }}
                    onClick={download}
                >
                    <Icon name={"download"}/>Download XLSX
                </Button>
            </ButtonGroup>
            <ReportScriptSelection/>
            <ReportScriptResultTabMenu
                response={response}
                context={context}
                xRequestId={xRequestId}
            />

        </div>
    }

    return <>
        <ChangeableSplitter
            name={"ReportScriptPage"}
            initiallyHorizontal={true}
            topOrLeft={drawTop()}
            bottomOrRight={drawBottom()}
        />
    </>
}